import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { parentInvitationApi } from '@/services/api';
import { usePageTitle } from '@/hooks/usePageTitle';
import LoadingSpinner from '@/components/LoadingSpinner';
import { FeatureModuleGuard } from '@/components/FeatureModuleGuard';
import { PhoneNumberInput } from '@/components/forms/PhoneNumberInput';
import { buildTenantUrl } from '@/utils/environment';
import { useNameFormat } from '@/utils/nameFormat';
import { formatApiError } from '@/utils/errorClassifier';
import { LanguageToggle } from '@/components/parent/LanguageToggle';

interface TokenValidation {
  valid: boolean;
  user?: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
  schoolName?: string;
  error?: string;
}

export default function ParentAcceptInvitation() {
  const { t, i18n } = useTranslation(['parent', 'common']);
  usePageTitle(t('invitation.pageTitle'));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { formatParentName } = useNameFormat();

  const token = searchParams.get('token')?.trim() ?? null;

  const [validation, setValidation] = useState<TokenValidation | null>(null);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setValidating(false);
      setError(t('invitation.noToken'));
      return;
    }

    setValidating(true);
    setError(null);

    parentInvitationApi
      .validateToken(token)
      .then((response) => {
        if (response.valid) {
          setValidation(response);
        } else {
          setError(response.error || t('invitation.invalidToken'));
        }
      })
      .catch(() => {
        setError(t('invitation.failedToValidate'));
      })
      .finally(() => {
        setValidating(false);
      });
  }, [token, t]);

  const acceptMutation = useMutation({
    mutationFn: async () => {
      if (!token) throw new Error('No token');
      const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
      return parentInvitationApi.acceptInvitation(token, password, phoneNumber || undefined, lang);
    },
    onSuccess: (response) => {
      // Redirect to login on the correct subdomain
      const loginUrl = buildTenantUrl(response.subdomain, '/login?message=password_set');
      window.location.href = loginUrl;
    },
    onError: (error: unknown) => {
      setError(formatApiError(error));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError(t('invitation.passwordsDoNotMatch'));
      return;
    }

    if (password.length < 8) {
      setError(t('invitation.passwordMinLength'));
      return;
    }

    setError(null);
    acceptMutation.mutate();
  };

  const containerStyle: React.CSSProperties = {
    maxWidth: '500px',
    margin: '1rem auto',
    padding: '1.5rem',
    backgroundColor: 'var(--bg-primary)',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--border-color)',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '0.75rem',
    backgroundColor: 'var(--primary-color)',
    color: 'var(--on-primary-color)',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '36px',
    width: '100%',
  };

  if (validating) {
    return <LoadingSpinner />;
  }

  // Error state (invalid token, expired, etc.)
  if (error && !validation) {
    return (
      <FeatureModuleGuard
        module="parentPortal"
        fallback={
          <div style={containerStyle}>
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>{t('invitation.portalUnavailable')}</p>
            </div>
          </div>
        }
      >
        <div style={containerStyle}>
          <LanguageToggle />
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h1>{t('invitation.title')}</h1>
          </div>

          <div
            style={{
              padding: '1rem',
              backgroundColor: 'var(--error-bg)',
              color: 'var(--error-text)',
              borderRadius: 'var(--radius)',
              marginBottom: '1rem',
              textAlign: 'center',
            }}
          >
            <p style={{ margin: 0 }}>{error}</p>
          </div>

          <p style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            {t('invitation.contactSchool')}
          </p>

          <div style={{ marginTop: '1.5rem' }}>
            <button onClick={() => navigate('/login')} style={buttonStyle}>
              {t('invitation.goToLogin')}
            </button>
          </div>
        </div>
      </FeatureModuleGuard>
    );
  }

  return (
    <FeatureModuleGuard
      module="parentPortal"
      fallback={
        <div style={containerStyle}>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>{t('invitation.portalUnavailable')}</p>
          </div>
        </div>
      }
    >
      <div style={containerStyle}>
        <LanguageToggle />
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1>{t('invitation.setPasswordTitle')}</h1>
          {validation?.schoolName && (
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{validation.schoolName}</p>
          )}
        </div>

        {validation?.user && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: 'var(--radius)',
              marginBottom: '1.5rem',
              textAlign: 'center',
            }}
          >
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              {t('invitation.settingUpAccountFor')}
            </p>
            <h2 style={{ margin: '0.5rem 0', fontSize: '1.25rem' }}>
              {validation.user.firstName && validation.user.lastName
                ? formatParentName(validation.user.firstName, validation.user.lastName)
                : validation.user.email}
            </h2>
            {(validation.user.firstName || validation.user.lastName) && (
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>{validation.user.email}</p>
            )}
          </div>
        )}

        {error && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: 'var(--error-bg)',
              color: 'var(--error-text)',
              borderRadius: 'var(--radius)',
              marginBottom: '1rem',
            }}
          >
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="form-group">
            <label>{t('common:form.password')} <span style={{ color: 'var(--error-color)' }}>*</span></label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="form-input"
              autoComplete="new-password"
            />
            <p className="help-text">{t('invitation.passwordMinLengthHint')}</p>
          </div>

          <div className="form-group">
            <label>{t('common:form.confirmPassword')} <span style={{ color: 'var(--error-color)' }}>*</span></label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="form-input"
              autoComplete="new-password"
            />
          </div>

          <div className="form-group">
            <label>{t('common:form.phoneNumber')}</label>
            <PhoneNumberInput value={phoneNumber} onChange={setPhoneNumber} placeholder="(555) 555-5555" />
            <p className="help-text" style={{ fontSize: '0.7rem' }}>
              {t('invitation.phoneHelpText')}{' '}
              <Trans
                t={t}
                i18nKey="invitation.seeSmSTerms"
                components={{
                  smsTermsLink: <a href="/sms-terms" style={{ color: 'var(--primary-color)' }} />,
                }}
              />
            </p>
          </div>

          <button type="submit" disabled={acceptMutation.isPending} style={buttonStyle}>
            {acceptMutation.isPending ? t('invitation.settingPassword') : t('invitation.setPasswordButton')}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.875rem' }}>
          {t('common:auth.alreadyHaveAccount')}{' '}
          <a href="/login" style={{ color: 'var(--primary-color)' }}>
            {t('common:auth.logIn')}
          </a>
        </p>
      </div>
    </FeatureModuleGuard>
  );
}
